import { LostFoundType, TalkTypeCode } from "@/types";
import { IBaseResponse, IBaseParser } from "../shared/baseParser";
import useGetAccessToken from "@/use/useGetAccessToken";
import { BbsTypeCode, SERVICE_UID } from "@/types/index";
import {
  INewRouteApplicationDetail,
  INewRouteApplicationsCollection,
  INoticeInfoData,
  IQnaInfosCollection,
  IVocCollection,
  IVocDetail,
} from "@/domain/entities/bbs";
import { IResultData } from "@/domain/entities/shared/result";
import { Result, combine, err } from "neverthrow";
import { IHttpService } from "@/repositories/shared/httpService";
import { HttpError } from "@/repositories/shared/errors/httpError";
import { ParseError } from "@/repositories/shared/errors/parseError";
import {
  IBbsRepository,
  HttpNoticeInfoResponse,
  INoticeInfoParser,
  HttpQnaListResponse,
  HttpNewRouteApplicationsResponse,
  HttpNewRouteApplicationDetailResponse,
  INewRouteApplicationParser,
  INewRouteApplicationDetailParser,
  IQnaInfosCollectionParser,
  HttpQnaDetailResponse,
  IQnaInfoDetailParser,
  IVocCollectionParser,
  IVocDetailParser,
  HttpVocListResponse,
  HttpVocDetailResponse,
  ILostFoundsCollectionParser,
  HttpLostFoundListResponse,
  HttpLostFoundDetailResponse,
  ILostFoundDetailParser,
} from "./bbsRepository.types";
import { inject, injectable } from "inversify";
import TYPES from "@/ioc/identifiers";
import useGetProtocolHost from "@/use/useGetProtocolHost";
import { IQnaDetail } from "@/domain/entities/bbs/qnaDetail.types";
import useGetServiceInfo from "@/use/useGetServiceInfo";
import { ILostFoundsCollection } from "@/domain/entities/bbs/lostFound/lostFound.types";
import { ILostFoundDetail } from "@/domain/entities/bbs/lostFound/lostFoundDetail.types";

@injectable()
export class BbsRepository implements IBbsRepository {
  @inject(TYPES.INoticeInfoParser)
  private noticeInfoParser!: INoticeInfoParser;

  @inject(TYPES.INewRouteApplicationParser)
  private newRouteApplicationParser!: INewRouteApplicationParser;

  @inject(TYPES.INewRouteApplicationDetailParser)
  private newRouteApplicationDetailParser!: INewRouteApplicationDetailParser;

  @inject(TYPES.IQnaInfosCollectionParser)
  private qnaInfosCollectionParser!: IQnaInfosCollectionParser;

  @inject(TYPES.IQnaInfoDetailParser)
  private qnaInfoDetailParser!: IQnaInfoDetailParser;

  @inject(TYPES.IVocCollectionParser)
  private vocCollectionParser!: IVocCollectionParser;

  @inject(TYPES.IVocDetailParser)
  private vocDetailParser!: IVocDetailParser;

  @inject(TYPES.ILostFoundsCollectionParser)
  private lostFoundsCollectionParser!: ILostFoundsCollectionParser;

  @inject(TYPES.ILostFoundDetailParser)
  private lostFoundDetailParser!: ILostFoundDetailParser;

  @inject(TYPES.IBaseParser)
  private baseParser!: IBaseParser;

  private isPublicTalkApi = useGetServiceInfo()?.isPublicTalkApi ?? false;

  private httpService: IHttpService;

  constructor(@inject(TYPES.IHttpService) httpService: IHttpService) {
    this.httpService = httpService;
    this.httpService.initService(
      `${useGetProtocolHost()}/aibos/client/api/${
        process.env.VUE_APP_API_VERSION
      }/service/${SERVICE_UID}/talk`
    );
  }

  private getHeaders(
    isPublic: boolean,
    extraHeaders?: { [key: string]: string }
  ) {
    const token = useGetAccessToken();

    return isPublic
      ? extraHeaders
      : { ...extraHeaders, Authorization: `Bearer ${token}` };
  }

  async getNoticeInfos(): Promise<
    Result<INoticeInfoData[], ParseError | HttpError>
  > {
    const parseTo = (response: HttpNoticeInfoResponse) => {
      const noticeInfos = response.data.map(this.noticeInfoParser.toDomain);
      return combine(noticeInfos);
    };

    const publicPath = this.isPublicTalkApi ? "/public" : "";

    return await this.httpService.get<
      HttpNoticeInfoResponse,
      INoticeInfoData[]
    >(
      {
        url: "?talkBbsCode=BBS-NOTICE&sortOrder=topYn desc, savedTs desc",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }${publicPath}/service/${SERVICE_UID}/talk`,
          headers: this.getHeaders(this.isPublicTalkApi),
        },
      },
      { parseTo }
    );
  }

  async readNoticeInfo(
    id: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: `/${id}/read`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getNewRouteApplications(
    searchText: string,
    offset: number,
    limit = 20,
    myPostOnly = false,
    todayYn = false
  ): Promise<Result<INewRouteApplicationsCollection, ParseError | HttpError>> {
    const parseTo = (response: HttpNewRouteApplicationsResponse) => {
      return this.newRouteApplicationParser.toDomain(response);
    };

    const params = {
      searchText,
      offset,
      limit,
      myPostOnly,
      todayYn,
    };

    return await this.httpService.get<
      HttpNewRouteApplicationsResponse,
      INewRouteApplicationsCollection
    >(
      {
        url: "/line?talkTypeCode=TKT-POST",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params,
        },
      },
      { parseTo }
    );
  }

  async getNewRouteApplicationDetail(
    uid: string
  ): Promise<Result<INewRouteApplicationDetail, ParseError | HttpError>> {
    const parseTo = (response: HttpNewRouteApplicationDetailResponse) => {
      return this.newRouteApplicationDetailParser.toDomain(response.data);
    };

    return await this.httpService.get<
      HttpNewRouteApplicationDetailResponse,
      INewRouteApplicationDetail
    >(
      {
        url: `/line/${uid}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async applyNewRouteApplication(
    newRouteDetail: INewRouteApplicationDetail
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    const httpNewRouteDetail =
      this.newRouteApplicationDetailParser.fromDomain(newRouteDetail);

    if (httpNewRouteDetail instanceof ParseError) {
      return err(httpNewRouteDetail);
    }

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/line",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
        data: httpNewRouteDetail,
      },
      { parseTo }
    );
  }

  async editNewRouteApplication(
    talkUid: string,
    newRouteDetail: INewRouteApplicationDetail
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    const httpNewRouteDetail =
      this.newRouteApplicationDetailParser.fromDomain(newRouteDetail);

    if (httpNewRouteDetail instanceof ParseError) {
      return err(httpNewRouteDetail);
    }

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: `/line/${talkUid}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
        data: httpNewRouteDetail,
      },
      { parseTo }
    );
  }

  async deleteNewRouteApplication(
    routeApplicationUid: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: `/line/${routeApplicationUid}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async receiveNewRouteApplicationResult(
    routeApplicationUid: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: `/line/${routeApplicationUid}/receive`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getRecentlyUsedLines(
    boardDateFrom?: string,
    boardDateTo?: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    const params = {
      boardDateFrom,
      boardDateTo,
    };

    return await this.httpService.get<IBaseResponse, IResultData>(
      {
        url: "/board",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params,
        },
      },
      { parseTo }
    );
  }

  async getQnaList(
    bbsType: BbsTypeCode,
    talkTypeCode: TalkTypeCode,
    searchText: string,
    myPostOnly: boolean,
    offset: number,
    limit = 20,
    todayYn = false
  ): Promise<Result<IQnaInfosCollection, ParseError | HttpError>> {
    const parseTo = (response: HttpQnaListResponse) => {
      return this.qnaInfosCollectionParser.toDomain(response);
    };

    const params = {
      talkBbsCode: bbsType,
      talkTypeCode,
      searchText,
      myPostOnly,
      offset,
      limit,
      todayYn,
      sortOrder: "createdTs desc",
    };

    return await this.httpService.get<HttpQnaListResponse, IQnaInfosCollection>(
      {
        url: "",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params,
        },
      },
      { parseTo }
    );
  }

  async getQnaDetail(
    uid: string
  ): Promise<Result<IQnaDetail, ParseError | HttpError>> {
    const parseTo = (response: HttpQnaDetailResponse) => {
      return this.qnaInfoDetailParser.toDomain(response.data);
    };

    return await this.httpService.get<HttpQnaDetailResponse, IQnaDetail>(
      {
        url: `/${uid}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async createQnaDetail(
    qnaDetail: IQnaDetail,
    formData: FormData
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response.data);
    };

    const jsonBlob = new Blob([JSON.stringify(qnaDetail)], {
      type: "application/json",
    });

    formData.append("param", jsonBlob);

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "",
        config: {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
        data: formData,
      },
      { parseTo }
    );
  }

  async updateQnaDetail(
    qnaDetail: IQnaDetail,
    formData: FormData
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response.data);
    };

    const httpParams = this.qnaInfoDetailParser.fromDomain(qnaDetail);

    const jsonBlob = new Blob([JSON.stringify(httpParams)], {
      type: "application/json",
    });

    formData.append("param", jsonBlob);

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: `${qnaDetail.uid}`,
        config: {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
        data: formData,
      },
      { parseTo }
    );
  }

  async deleteQnaDetail(
    talkUid: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response.data);
    };

    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: `${talkUid}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getVocList(
    codeGroup: string,
    searchText: string,
    offset: number,
    limit = 20,
    todayYn = false,
    myPostOnly = true
  ): Promise<Result<IVocCollection, ParseError | HttpError>> {
    const parseTo = (response: HttpVocListResponse) => {
      return this.vocCollectionParser.toDomain(response);
    };

    const params = {
      vocTypeCodeGroup: codeGroup,
      searchText,
      myPostOnly,
      offset,
      limit,
      todayYn,
      sortOrder: "createdTs desc",
    };

    return await this.httpService.get<HttpVocListResponse, IVocCollection>(
      {
        url: "/voc",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params,
        },
      },
      { parseTo }
    );
  }

  async getVocDetail(
    uid: string
  ): Promise<Result<IVocDetail, ParseError | HttpError>> {
    const parseTo = (response: HttpVocDetailResponse) => {
      return this.vocDetailParser.toDomain(response.data);
    };

    return await this.httpService.get<HttpVocDetailResponse, IVocDetail>(
      {
        url: `/voc/${uid}`,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async createVocDetail(
    vocDetail: IVocDetail,
    formData: FormData
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response.data);
    };

    const jsonBlob = new Blob(
      [JSON.stringify(this.vocDetailParser.fromDomain(vocDetail))],
      {
        type: "application/json",
      }
    );

    formData.append("param", jsonBlob);

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/voc",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
        data: formData,
      },
      { parseTo }
    );
  }

  async updateVocDetail(
    vocDetail: IVocDetail,
    formData: FormData
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response.data);
    };

    const httpVocDetail = this.vocDetailParser.fromDomain(vocDetail);

    const jsonBlob = new Blob([JSON.stringify(httpVocDetail)], {
      type: "application/json",
    });

    formData.append("param", jsonBlob);

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: `/voc/${vocDetail.uid}`,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
        data: formData,
      },
      { parseTo }
    );
  }

  async deleteVocDetail(
    uid: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response.data);
    };

    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: `/voc/${uid}`,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getLostFoundList(
    lostFoundType: LostFoundType,
    searchText: string,
    offset: number,
    limit = 20,
    todayYn = false
  ): Promise<Result<ILostFoundsCollection, ParseError | HttpError>> {
    const parseTo = (response: HttpLostFoundListResponse) => {
      return this.lostFoundsCollectionParser.toDomain(response);
    };

    const params = {
      lostFoundType,
      searchText,
      offset,
      limit,
      todayYn,
    };

    return await this.httpService.get<
      HttpLostFoundListResponse,
      ILostFoundsCollection
    >(
      {
        url: "/lost",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params,
        },
      },
      { parseTo }
    );
  }

  async getLostFoundDetail(
    uid: string
  ): Promise<Result<ILostFoundDetail, ParseError | HttpError>> {
    const parseTo = (response: HttpLostFoundDetailResponse) => {
      return this.lostFoundDetailParser.toDomain(response);
    };

    return await this.httpService.get<
      HttpLostFoundDetailResponse,
      ILostFoundDetail
    >(
      {
        url: `/lost/${uid}`,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async createLostFoundDetail(
    lostFoundDetail: ILostFoundDetail
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response.data);
    };

    const httpLostFoundDetail =
      this.lostFoundDetailParser.fromDomain(lostFoundDetail);

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/lost",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
        data: httpLostFoundDetail,
      },
      { parseTo }
    );
  }

  async updateLostFoundDetail(
    lostFoundDetail: ILostFoundDetail
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response.data);
    };

    const httpLostFoundDetail =
      this.lostFoundDetailParser.fromDomain(lostFoundDetail);

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: `/lost/${lostFoundDetail.uid}`,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
        data: httpLostFoundDetail,
      },
      { parseTo }
    );
  }

  async deleteLostFoundDetail(
    uid: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response.data);
    };

    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: `/lost/${uid}`,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }
}
